<!--
  ConfirmSheet:F-4 二次确认弹窗主内容(嵌在 PanelModal 内)
  UI-SPEC §4.6
  Edit / Confirm 两按钮,行列表由 variant + rows 渲染
  variant:physical(3 行 Prize/Name/Email)/ ticket(6 行 含 Match)/ concert(5 行 无 Match)
  highlight=true 行 value 走橙色 #FF6900(Ticket 数量行)
  P05 TicketModal 会复用本组件
-->
<template>
  <div
    class="confirm-sheet"
    :class="[
      `page-${pageType}`,
      { embedded },
    ]"
  >
    <h2
      v-if="showHeader"
      class="title"
    >
      {{ t('Modal.confirmTitle') }}
    </h2>
    <p
      v-if="showHeader"
      class="subtitle"
    >
      {{ subtitle }}
    </p>
    <div class="info-panel">
      <div
        v-for="(row, idx) in rows"
        :key="idx"
        class="row"
        :class="{ highlight: row.highlight }"
      >
        <span class="row-label">{{ row.label }}</span>
        <span class="row-value">{{ row.value }}</span>
      </div>
    </div>
    <div
      v-if="showActions"
      class="cta"
    >
      <button
        type="button"
        class="btn-ghost"
        @click="$emit('edit')"
      >
        {{ t('Modal.edit') }}
      </button>
      <button
        type="button"
        class="btn-primary"
        @click="$emit('confirm')"
      >
        {{ t('Modal.confirm') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import t from '../utils/i18n';
type Row = {
  label: string;
  value: string;
  highlight?: boolean;
};
type Props = {
  pageType: 'PC' | 'H5';
  variant: 'physical' | 'ticket' | 'concert';
  rows: Row[];
  subtitle: string;
  embedded?: boolean;
  showHeader?: boolean;
  showActions?: boolean;
};
const __sfc_main = {};
__sfc_main.props = {
  pageType: {
    key: "pageType",
    required: true,
    type: String
  },
  variant: {
    key: "variant",
    required: true,
    type: String
  },
  rows: {
    key: "rows",
    required: true,
    type: Array
  },
  subtitle: {
    key: "subtitle",
    required: true,
    type: String
  },
  embedded: {
    key: "embedded",
    required: false,
    type: Boolean,
    default: false
  },
  showHeader: {
    key: "showHeader",
    required: false,
    type: Boolean,
    default: true
  },
  showActions: {
    key: "showActions",
    required: false,
    type: Boolean,
    default: true
  }
};
__sfc_main.setup = (__props, __ctx) => {
  return {
    t
  };
};
export default __sfc_main;
</script>

<style scoped>
.confirm-sheet {
  display: flex;
  flex-direction: column;
}

.confirm-sheet.embedded {
  padding: 0;
}

.info-panel {
  background: #f1f2f3;
}
.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.row + .row {
  border-top: 1px solid #e3e4e5;
}

.row-label {
  color: #626262;
}
.row-value {
  color: #121212;
}
.row.highlight .row-value {
  color: #ff6900;
}

/* ─── 按钮 ─────────────────────────────────────────────── */
.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: #ff6900;
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}
.btn-ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
  color: #1d1d1d;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}

/* ─── H5(rem)UI-SPEC §4.6.a ──────────────────────────── */
.page-H5 {
  padding: 0.5rem 0.4rem;
  gap: 0.32rem;
}
.page-H5.embedded {
  gap: 0.32rem;
  padding: 0;
}
.page-H5 .title {
  font-weight: 600;
  font-size: 0.5rem;
  line-height: 1.1em;
  color: #262626;
  margin: 0;
  text-align: center;
}
.page-H5 .subtitle {
  font-weight: 400;
  font-size: 0.3rem;
  line-height: 1.4em;
  color: #626262;
  margin: 0;
}
.page-H5 .info-panel {
  border-radius: 0.2rem;
  padding: 0.3rem;
  width: 5.9rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.page-H5 .row + .row {
  margin-top: 0.3rem;
  padding-top: 0.3rem;
}

.page-H5 .row-label {
  flex-shrink: 0;
}

.page-H5 .row-value {
  min-width: 0;
  margin-left: 0.2rem;
  line-height: 1.35;
  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.page-H5 .row-label,
.page-H5 .row-value {
  font-size: 0.3rem;
  font-weight: 400;
}
.page-H5 .cta {
  display: flex;
  justify-content: space-between;
  gap: 0.16rem;
}
.page-H5 .btn-ghost {
  width: 2.8rem;
  height: 0.9rem;
  border-radius: 2.24rem;
  border: 2px solid #1d1d1d;
  font-weight: 500;
  font-size: 0.3rem;
  color: #262626;
}
.page-H5 .btn-primary {
  width: 2.8rem;
  height: 0.9rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.3rem;
}

/* ─── PC UI-SPEC §4.6.b(容器 padding 已在 NoticeModal)── */
.page-PC {
  gap: 40px;
}
.page-PC.embedded {
  gap: 0;
}
.page-PC .title {
  font-weight: 600;
  font-size: 32px;
  color: #000000;
  margin: 0;
  text-align: left;
}
.page-PC .subtitle {
  display: none;
}
.page-PC .info-panel {
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #f1f2f3;
}

.page-PC .row + .row {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e3e4e5;
}

.page-PC .row-label {
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.35;
  color: #626262;
}
.page-PC .row-value {
  min-width: 0;
  margin-left: 20px;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.35;
  color: #121212;
  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.page-PC .cta {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 0;
}
.page-PC .btn-ghost {
  width: 160px;
  height: 60px;
  border-radius: 60px;
  border: 1px solid #1d1d1d;
  font-weight: 600;
  font-size: 24px;
  color: #1d1d1d;
}
.page-PC .btn-ghost + .btn-primary {
  margin-left: 10px;
}
.page-PC .btn-primary {
  width: 280px;
  height: 60px;
  border-radius: 60px;
  font-weight: 600;
  font-size: 24px;
}
</style>
